import React from "react";
import { DataSubType } from "oschema";

import {
  Field,
  inputKindFor,
  isBooleanField,
  isNumeric,
  isStructured,
  isTemporal,
  parsesAsNumber,
} from "./schema-field";

/* ------------------------------------------------------------------ dates */

const DATE_ONLY = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "2-digit",
});

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

/** Same call the form makes, so a `starts_on` column reads as a date in both. */
const isDateOnlyField = (field?: Field) =>
  !!field && isTemporal(field) && inputKindFor(field) === "date";

export const toDate = (value: unknown): Date | null => {
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value !== "string" || value.trim() === "") return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

/** "3 days ago" / "in 2 months", using the platform formatter. */
export const relativeTime = (date: Date): string => {
  const diffMs = date.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 365 * 24 * 3600e3],
    ["month", 30 * 24 * 3600e3],
    ["day", 24 * 3600e3],
    ["hour", 3600e3],
    ["minute", 60e3],
  ];

  for (const [unit, ms] of units) {
    if (abs >= ms) return rtf.format(Math.round(diffMs / ms), unit);
  }
  return rtf.format(Math.round(diffMs / 1000), "second");
};

export const formatDate = (value: unknown, field?: Field): string => {
  const date = toDate(value);
  if (!date) return String(value ?? "");
  const dateOnly =
    isDateOnlyField(field) ||
    (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim()));
  return dateOnly ? DATE_ONLY.format(date) : DATE_TIME.format(date);
};

/* ---------------------------------------------------------------- numbers */

const NUMBER = new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 });
const DECIMAL = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const toNumber = (value: unknown): number | null => {
  if (typeof value === "number") return isNaN(value) ? null : value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return isNaN(n) ? null : n;
  }
  return null;
};

/**
 * Percentages come out of Postgres either as a fraction (0.125) or as a whole
 * number (12.5). The sampled `max` tells us which convention this column uses.
 */
export const percentageAsPercent = (n: number, field?: Field): number =>
  (field?.max ?? 1) <= 1 ? n * 100 : n;

export const formatNumber = (value: unknown, field?: Field): string => {
  // A string that only *looks* numeric to `Number()` (a phone number, an
  // account code with a leading zero) must survive untouched.
  if (!parsesAsNumber(value)) return String(value ?? "");
  const n = toNumber(value);
  if (n === null) return String(value ?? "");

  const kind = field ? inputKindFor(field) : undefined;
  if (kind === "percentage") {
    const pct = percentageAsPercent(n, field);
    return `${NUMBER.format(Number(pct.toFixed(2)))}%`;
  }
  if (kind === "currency") {
    const formatted = DECIMAL.format(Math.abs(n));
    return `${n < 0 ? "-" : ""}$${formatted}`;
  }

  switch (field?.subType) {
    case DataSubType.DECIMAL:
      return DECIMAL.format(n);
    case DataSubType.INTEGER:
      return NUMBER.format(n);
    default:
      return Number.isInteger(n) ? NUMBER.format(n) : DECIMAL.format(n);
  }
};

/* ------------------------------------------------------------------- json */

export const prettyJson = (value: unknown): string => {
  if (typeof value === "string") {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/** One-line preview: `{ status: "ok", retries: 3 }` or `[3 items]`. */
export const jsonPreview = (value: unknown): string => {
  let parsed: any = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return value;
    }
  }
  if (parsed === null || typeof parsed !== "object") return String(parsed);
  if (Array.isArray(parsed)) {
    return parsed.length === 1 ? "[1 item]" : `[${parsed.length} items]`;
  }
  const keys = Object.keys(parsed);
  if (keys.length === 0) return "{}";
  const body = keys
    .slice(0, 4)
    .map((k) => {
      const v = parsed[k];
      const rendered =
        v === null
          ? "null"
          : typeof v === "object"
          ? Array.isArray(v)
            ? "[…]"
            : "{…}"
          : typeof v === "string"
          ? `"${v.length > 18 ? v.slice(0, 18) + "…" : v}"`
          : String(v);
      return `${k}: ${rendered}`;
    })
    .join(", ");
  return `{ ${body}${keys.length > 4 ? ", …" : ""} }`;
};

/* ------------------------------------------------------------ plain value */

/**
 * The canonical "what does this value read as" function. Used for CSV export,
 * tooltips, copy-to-clipboard and as the fallback inside cell renderers, so
 * every surface agrees on how a value is spelled.
 */
export const formatValue = (value: unknown, field?: Field): string => {
  if (value === null || value === undefined) return "";
  if (isBooleanField(field) || typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (isTemporal(field) && !parsesAsNumber(value)) return formatDate(value, field);
  if (isStructured(field) || (typeof value === "object" && value !== null)) {
    return jsonPreview(value);
  }
  if ((isNumeric(field) && parsesAsNumber(value)) || typeof value === "number") {
    return formatNumber(value, field);
  }

  const str = String(value);
  // No schema help: still recognise an ISO timestamp rather than dumping it raw.
  if (!field && /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})?/.test(str)) {
    if (toDate(str)) return formatDate(str);
  }
  return str;
};

/* --------------------------------------------------------------- swatches */

const SEMANTIC_TONES: Array<[RegExp, Tone]> = [
  [
    /^(active|approved|complete[d]?|done|paid|success|open|yes|enabled|live)$/i,
    "success",
  ],
  [/^(pending|in[\s_-]?progress|review|draft|todo|waiting|partial)$/i, "warning"],
  [
    /^(failed|error|rejected|cancell?ed|blocked|overdue|inactive|no|disabled|closed)$/i,
    "danger",
  ],
];

export type Tone = "success" | "warning" | "danger" | "neutral";

export const toneFor = (value: string): Tone => {
  for (const [pattern, tone] of SEMANTIC_TONES) {
    if (pattern.test(value.trim())) return tone;
  }
  return "neutral";
};

const TONE_STYLES: Record<Tone, React.CSSProperties> = {
  success: {
    color: "var(--od-success)",
    backgroundColor: "color-mix(in srgb, var(--od-success) 14%, transparent)",
    borderColor: "color-mix(in srgb, var(--od-success) 34%, transparent)",
  },
  warning: {
    color: "var(--od-warning)",
    backgroundColor: "color-mix(in srgb, var(--od-warning) 16%, transparent)",
    borderColor: "color-mix(in srgb, var(--od-warning) 36%, transparent)",
  },
  danger: {
    color: "var(--od-danger)",
    backgroundColor: "color-mix(in srgb, var(--od-danger) 14%, transparent)",
    borderColor: "color-mix(in srgb, var(--od-danger) 34%, transparent)",
  },
  neutral: {
    color: "var(--od-text)",
    backgroundColor: "var(--od-surface-2)",
    borderColor: "var(--od-border)",
  },
};

export const Badge: React.FC<{ value: string; tone?: Tone }> = ({
  value,
  tone,
}) => (
  <span
    className="inline-flex max-w-full items-center truncate rounded-full border px-2 py-[1px] text-xs font-medium leading-5"
    style={TONE_STYLES[tone ?? toneFor(value)]}
    title={value}
  >
    {value}
  </span>
);
